import { reactive, ref } from "@vue/composition-api";
import _axios from "@/axios";
import { handleFormErrors } from "@/composables/form-error";
import router from "@/router";
import { showSnackBar } from "@/composables/snackbar";
import { cloneDeep } from "lodash";

const currentGroup = ref(null);
const criteriaGroup = reactive({
  name: "",
  versions: [],
});

export function useGroup() {
  let data = reactive({
    options: {},
    firstLoad: true,
    isLoading: false,
    groups: {},
    groupForm: {},
    createGroupDialog: false,
    editGroupDialog: false,
    portalConfigurationDialog: false,
    portalConfigurationFormRef: null,
    portalPresentationModelDialog: false,
    portalPresentationThemeDialog: false,
    portalPresentationOtherDialog: false,
    portalConfigurationLanguageDialog: false,
    portalConfigurationPresentationDialog: false,
    portalConfigurationOtherDialog: false,
    groupFormRef: null,
    groupFormValid: false,
    rules: {
      languagesRules: [
        (v) => !!v || "Il faut selectionner au moins une langue",
      ],
      default_languageRules: [(v) => !!v || "Il faut une langue par défault"],
      partnerRules: [(v) => !!v || "Il faut sélectionner un partenaire"],
      descriptionRules: [
        (v) => !!v || "Il faut entrer une description",
        (v) =>
          (v != null && v.length > 1 && v.length <= 200) ||
          "Entre 2 et 200 caractères",
      ],
    },
  });

  const closeAllDialogs = () => {
    data.portalPresentationModelDialog = false;
    data.portalPresentationThemeDialog = false;
    data.portalPresentationOtherDialog = false;
    data.portalConfigurationLanguageDialog = false;
    data.portalConfigurationPresentationDialog = false;
    data.portalConfigurationOtherDialog = false;
  };

  async function listGroups(criteria) {
    data.isLoading = true;

    const params = new URLSearchParams();

    params.set("page", data.options.page ?? 1);
    params.set("itemsPerPage", data.options.itemsPerPage ?? 1000);

    if (data.options.sortBy && data.options.sortBy.length > 0) {
      if (data.options.sortDesc[0]) {
        params.set("order[desc]", data.options.sortBy[0]);
      } else {
        params.set("order[asc]", data.options.sortBy[0]);
      }
    }

    if (criteria != null && typeof criteria == "string" && criteria != "") {
      params.set("searchTerm", criteria);
      params.set("version", 3); // search only group for V5 in autocomplete
    } else if (criteria != null) {
      if (criteria.name.length > 1) {
        params.set("searchTerm", criteria.name);
      }
      if (criteria.versions.length > 0) {
        params.set("version", criteria.versions.join("|"));
      }
    }

    return _axios
      .get("/list-group?" + params.toString())
      .then((response) => {
        data.groups = response.data;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      })
      .finally(() => {
        data.isLoading = false;
        data.firstLoad = false;
      });
  }

  function showGroup(id) {
    data.isLoading = true;

    return _axios
      .get("/group/" + id)
      .then((response) => {
        const group = response.data.data;

        currentGroup.value = group;
      })
      .catch(() => {
        alert(
          "Une erreur est survenue, si cela se reproduit contactez le support"
        );
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function updatePortalConfiguration(
    id,
    portalConfigurationFom,
    isInherit = false,
    isBuiltIn = false,
    portalProfile = null,
    portalConfigurationFormRef = null
  ) {
    data.isLoading = true;
    if (portalConfigurationFormRef != null) {
      const isValid = portalConfigurationFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        return;
      }
    }

    const formData = {};
    console.log(isInherit);
    if (!isBuiltIn) {
      formData["portal_configuration"] = cloneDeep(portalConfigurationFom);
    }

    if (formData.portal_configuration.full_presentation.payment == null) {
      formData.portal_configuration.full_presentation = {};
    }
    if (formData.portal_configuration.basic_presentation.payment == null) {
      formData.portal_configuration.basic_presentation = {};
    }

    if (isBuiltIn && portalProfile != null) {
      formData["builtin_id"] = portalProfile.id;
    }

    return _axios
      .put(`/update-group-configuration/${id}`, formData)
      .then((response) => {
        const group = response.data.data;
        currentGroup.value = group;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, data.rules, portalConfigurationFormRef);
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function createGroup() {
    data.isLoading = true;
    if (data.groupFormRef != null) {
      const isValid = data.groupFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        return;
      }
    }

    return _axios
      .post("/create-group", data.groupForm)
      .then((response) => {
        data.createGroupDialog = false;
        router.push({
          name: "groupe-details",
          params: { id: response.data.data.id },
        });
        showSnackBar();
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, data.rules, data.groupFormRef);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function updateGroup(group) {
    data.isLoading = true;
    if (data.groupFormRef != null) {
      const isValid = data.groupFormRef.validate();
      if (!isValid) {
        data.isLoading = false;
        return;
      }
    }

    return _axios
      .put("/group/" + group.id, data.groupForm)
      .then((response) => {
        data.editGroupDialog = false;
        showSnackBar();
        currentGroup.value = response.data.data;
      })
      .catch((error) => {
        const formErrors = error.response.data.violations;
        handleFormErrors(formErrors, data.rules, data.dataFormRef);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function deleteGroup(idGroup) {
    return _axios
      .delete(`/group/${idGroup}`)
      .then(() => {
        router.push({ name: "groupes" });
      })
      .catch(() => {
        alert(
          "Un problème est survenu lors de la suppression du groupe. Si le problème persiste veuillez contacter le support."
        );
      });
  }

  function updatePortalConfigurationModel(id, formData) {
    return _axios
      .put(`/update-group-model/${id}`, formData)
      .then((response) => {
        const group = response.data.data;
        currentGroup.value = group;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  function updatePortalConfigurationSparse(id, formData) {
    if (
      formData.full_presentation != null &&
      formData.full_presentation.payment == null
    ) {
      formData.full_presentation = {};
    }
    if (
      formData.basic_presentation != null &&
      formData.basic_presentation.payment == null
    ) {
      formData.basic_presentation = {};
    }

    return _axios
      .put(`/update-group-configuration-sparse/${id}`, formData)
      .then((response) => {
        const group = response.data.data;
        currentGroup.value = group;
        showSnackBar();
        closeAllDialogs();
      })
      .catch((error) => {
        alert(error.response.data.detail);
      })
      .finally(() => {
        data.isLoading = false;
      });
  }

  return {
    currentGroup,
    data,
    listGroups,
    showGroup,
    updatePortalConfiguration,
    createGroup,
    updateGroup,
    criteriaGroup,
    deleteGroup,
    updatePortalConfigurationModel,
    updatePortalConfigurationSparse,
  };
}
